import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import AppFooter from '../components/AppFooter';
import { Button } from '../components/ui/button';
import { donationsApi } from '../lib/api';

/**
 * Where GoCardless's hosted Billing Request Flow redirects back to
 * (redirect_uri, set in POST /api/donations/create). GoCardless appends an
 * id to this URL identifying the completed flow — we read whichever of
 * billing_request_id / billing_request_flow_id is present (not pinned down
 * with certainty which one it uses — see TODO.md "Donations") and let the
 * backend resolve it either way. See POST /api/donations/confirm and
 * _ensure_subscription in backend/app/routers/donations.py, which is what
 * actually sets up the recurring subscription for a monthly donation once
 * its mandate is fulfilled.
 */
export const DonateThankYou = () => {
  const [searchParams] = useSearchParams();
  const billingRequestId = searchParams.get('billing_request_id');
  const billingRequestFlowId = searchParams.get('billing_request_flow_id');
  const [state, setState] = useState('loading'); // loading | fulfilled | pending | error
  const [frequency, setFrequency] = useState('one_off');

  useEffect(() => {
    if (!billingRequestId && !billingRequestFlowId) {
      setState('error');
      return;
    }
    donationsApi.confirm({
      billing_request_id: billingRequestId || undefined,
      billing_request_flow_id: billingRequestFlowId || undefined,
    })
      .then(({ data }) => {
        setFrequency(data?.frequency || 'one_off');
        setState(data?.status === 'fulfilled' ? 'fulfilled' : 'pending');
      })
      .catch(() => setState('error'));
  }, [billingRequestId, billingRequestFlowId]);

  return (
    <div className="min-h-screen bg-surface text-ink" data-testid="donate-thank-you-page">
      <PublicHeader />

      <section className="mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-20 lg:px-12">
        <div className="max-w-xl mx-auto text-center space-y-5">
          {state === 'loading' && (
            <>
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-ink-55" aria-hidden="true" />
              <p className="text-ink-70">Confirming your donation…</p>
            </>
          )}

          {state === 'fulfilled' && (
            <>
              <h1 className="font-heading text-[40px] font-bold tracking-[-0.035em] leading-[1.05]">
                Thank you.
              </h1>
              <p className="text-[17px] text-ink-70 leading-relaxed">
                {frequency === 'monthly'
                  ? "Your monthly donation is set up. GoCardless will email you a confirmation, and a reminder a few days before each payment."
                  : 'Your donation is on its way. GoCardless will email you a confirmation.'}
              </p>
            </>
          )}

          {state === 'pending' && (
            <>
              <h1 className="font-heading text-[40px] font-bold tracking-[-0.035em] leading-[1.05]">
                Almost there.
              </h1>
              <p className="text-[17px] text-ink-70 leading-relaxed">
                Your bank is still confirming this with GoCardless. You do not need to do anything else —
                you will get an email confirmation shortly.
              </p>
            </>
          )}

          {state === 'error' && (
            <>
              <h1 className="font-heading text-[40px] font-bold tracking-[-0.035em] leading-[1.05]">
                We could not confirm that.
              </h1>
              <p className="text-[17px] text-ink-70 leading-relaxed">
                If money has left your account, you do not need to worry — GoCardless will still email you
                a receipt. If anything looks wrong, email{' '}
                <a href="mailto:hello@preciousdata.org.uk" className="text-brand hover:underline">
                  hello@preciousdata.org.uk
                </a>{' '}
                and we will sort it out.
              </p>
            </>
          )}

          <div className="pt-2">
            <Button asChild size="lg">
              <Link to="/donate" data-testid="thank-you-back-link">Back to Precious Data</Link>
            </Button>
          </div>
        </div>
      </section>

      <AppFooter />
    </div>
  );
};

export default DonateThankYou;
